import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';

import { Creature, Environment } from "@dndModels";

import DandDAPI from "@utilities/smart_api";

import LoadingBox from "@components/input/LoadingBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import CheckBox from "@components/input/CheckBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import CheckBoxIndex from "@dndComponents/CheckBoxIndex";
import DisplayStringWithReplacementsRecursive from '@dndComponents/display/DisplayStringWithReplacementsRecursive';

const api = DandDAPI.getInstance();

// Admin tool for quickly associating creatures with Environments.
// Only works from the light copies of the creatures,
// and only sets the environments field on the full and light docs.
export default function CreatureEnvironmentManager() {
  const [loading, setLoading] = useState(true);
  const [creatures, setCreatures] = useState<Creature[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [only_unassigned, setOnlyUnassigned] = useState(false);
  const [selected_ids, setSelectedIDs] = useState<string[]>([]);
  const [environment_id, setEnvironmentID] = useState("");
  const [saving, setSaving] = useState<{ action: string, done: number, total: number, failed: string[] } | null>(null);

  useEffect(() => {
    api.getSetOfObjects({ data_types: ["creature","environment"] }).then(res => {
      setCreatures((res.creature as Creature[]).sort((a, b) => a.name.localeCompare(b.name)));
      setEnvironments(res.environment as Environment[]);
      setLoading(false);
    });
  }, []);

  // Leftover values that aren't Environments don't count as an association
  const hasEnvironment = (creature: Creature) => creature.environments.some(id => environments.some(o => o._id === id));

  const environment = environments.find(o => o._id === environment_id);
  const selected_creatures = creatures.filter(o => selected_ids.includes(o._id));
  const to_add = selected_creatures.filter(o => !o.environments.includes(environment_id));
  const to_remove = selected_creatures.filter(o => o.environments.includes(environment_id));
  const running = !!saving && saving.done < saving.total;
  const env_name = environment ? environment.name : "";

  const updateEnvironments = async (action: "Adding" | "Removing", targets: Creature[]) => {
    const failed: string[] = [];
    setSaving({ action, done: 0, total: targets.length, failed });
    for (let i = 0; i < targets.length; ++i) {
      const creature = targets[i];
      const new_environments = action === "Adding" ?
        [...creature.environments, environment_id] :
        creature.environments.filter(o => o !== environment_id);
      try {
        if (await api.updateFieldsFullAndLight("creature", creature, { environments: new_environments })) {
          // Keep the cached light copy in sync with what was saved
          creature.environments = new_environments;
        } else {
          failed.push(creature.name);
        }
      } catch (ex) {
        console.error(ex);
        failed.push(creature.name);
      }
      setSaving({ action, done: i + 1, total: targets.length, failed: [...failed] });
    }
  };

  const environmentNames = (creature: Creature) => {
    return creature.environments.map(id => {
      const env = environments.find(o => o._id === id);
      return env ? env.name : `Unknown (${id})`;
    }).join(", ");
  };

  const renderHeader = (filter_matches: Creature[]) => {
    const counts: { [id: string]: number } = {};
    let unassigned_count = 0;
    for (const creature of filter_matches) {
      let has_one = false;
      for (const id of creature.environments) {
        if (environments.some(o => o._id === id)) {
          counts[id] = (counts[id] ?? 0) + 1;
          has_one = true;
        }
      }
      if (!has_one) {
        unassigned_count++;
      }
    }
    const sorted_environments = [...environments].sort((a, b) => a.name.localeCompare(b.name));
    return (
      <Grid container spacing={1}>
        <Grid size={12} className="header">
          Environment Counts (of {filter_matches.length} matching the filter)
        </Grid>
        <Grid size={12}>
          <Grid container spacing={0} direction="row">
            <Grid size={4} className="padded">
              <b>No Environments: {unassigned_count}</b>
            </Grid>
            { sorted_environments.map(env => (
              <Grid key={env._id} size={4}
                className={env._id === environment_id ? "padded clickable selected" : "padded clickable"}
                onClick={() => {
                  if (!running) {
                    setEnvironmentID(env._id);
                    setSaving(null);
                  }
                }}>
                {env.name}: {counts[env._id] ?? 0}
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid size={12}>
          <CheckBox
            name="Only Show Creatures with No Environments"
            value={only_unassigned}
            onChange={setOnlyUnassigned}
          />
        </Grid>
        <Grid size={12}>
          <SelectObjectBox
            name="Environment"
            data_type="environment"
            value={environment_id}
            disabled={running}
            onChange={(changed: string) => {
              setEnvironmentID(changed);
              setSaving(null);
            }}
          />
        </Grid>
        <Grid size={6}>
          <ButtonBox
            name={environment ? `Add ${env_name} to ${to_add.length} Selected` : "Choose an Environment"}
            disabled={running || !environment || to_add.length === 0}
            onClick={() => updateEnvironments("Adding", to_add)}
          />
        </Grid>
        <Grid size={6}>
          <ButtonBox
            name={environment ? `Remove ${env_name} from ${to_remove.length} Selected` : "Choose an Environment"}
            disabled={running || !environment || to_remove.length === 0}
            onClick={() => updateEnvironments("Removing", to_remove)}
          />
        </Grid>
        { saving &&
          <Grid size={12}>
            { running ?
              `${saving.action} ${env_name} ${saving.done} / ${saving.total}...` :
              `Done ${saving.action.toLowerCase()} ${env_name} for ${saving.total - saving.failed.length} of ${saving.total}.${saving.failed.length > 0 ? ` Failed: ${saving.failed.join(", ")}` : ""}`
            }
          </Grid>
        }
      </Grid>
    );
  };

  if (loading) {
    return <LoadingBox />;
  }

  return (
    <Grid container spacing={1}>
      <Grid size={12} className="header">
        Creature Environment Manager
      </Grid>
      <Grid size={12}>
        <CheckBoxIndex
          data_type="creature"
          objects={creatures}
          selected_ids={selected_ids}
          onChange={setSelectedIDs}
          disabled={running}
          quick_fields={["creature_type","subtypes","creature_group","min_cr","max_cr"]}
          extra_filter={only_unassigned ? (o => !hasEnvironment(o)) : undefined}
          header={renderHeader}
          details={creature => {
            const env_names = environmentNames(creature);
            return (
              <>
                { creature.creature_type_string }, CR { creature.cr_string }
                <br />
                { env_names.length > 0 ? `Environments: ${env_names}` : "No Environments" }
              </>
            );
          }}
          renderExpanded={creature => (
            <DisplayStringWithReplacementsRecursive display_string={`{creature_stat_block.${creature._id}}`} cfs={creature} />
          )}
        />
      </Grid>
    </Grid>
  );
}
